import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

/* ─────────────────────────────────────────────────────────
   Styles — mirrors the Nehish_Harikrishnan.docx layout:
   Times New Roman, section headings with bottom border,
   two-column bordered tables for skills & projects,
   bullet lists for summary & responsibilities.
───────────────────────────────────────────────────────── */
const S = {
  overlay: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1300, padding: '16px',
  },
  paper: {
    background: '#fff',
    width: '100%', maxWidth: '860px',
    maxHeight: '92vh', overflowY: 'auto',
    borderRadius: '3px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    fontFamily: "'Times New Roman', Times, serif",
    fontSize: '11pt',
    color: '#111',
    position: 'relative',
  },
  closeBtn: {
    position: 'sticky', top: 8, float: 'right',
    zIndex: 10, marginRight: 8, marginTop: 8,
    background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  body: { padding: '36px 52px 52px 52px', clear: 'both' },

  /* NAME BLOCK */
  nameBlock: { textAlign: 'center', marginBottom: '2px' },
  candidateName: {
    fontSize: '20pt', fontWeight: 'bold',
    letterSpacing: '1px', textTransform: 'uppercase', color: '#1a1a1a',
  },
  contactLine: {
    fontSize: '10pt', color: '#333', marginTop: '3px',
    display: 'flex', flexWrap: 'wrap',
    justifyContent: 'center', gap: '6px',
  },
  sep: { color: '#999' },

  /* DIVIDERS */
  hrThick: { border: 'none', borderTop: '2px solid #111', margin: '6px 0' },
  hrThin:  { border: 'none', borderTop: '1px solid #aaa', margin: '4px 0 10px 0' },

  /* SECTION HEADING — bold, uppercase, underlined (bottom border) */
  sectionHeading: {
    fontSize: '11pt', fontWeight: 'bold', textTransform: 'uppercase',
    letterSpacing: '0.4px', color: '#1a1a1a',
    margin: '16px 0 5px 0',
    borderBottom: '1.5px solid #1a1a1a', paddingBottom: '2px',
  },

  /* BULLET LIST */
  ul: { margin: '4px 0 0 0', paddingLeft: '20px', lineHeight: '1.6' },
  li: { marginBottom: '3px', fontSize: '10.5pt' },

  /* TWO-COLUMN TABLE (skills / projects) */
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '10.5pt', margin: '6px 0 10px 0' },
  tdLabel: {
    border: '1px solid #bbb', padding: '5px 10px',
    fontWeight: 'bold', width: '32%',
    backgroundColor: '#f5f5f5', verticalAlign: 'top',
  },
  tdValue: {
    border: '1px solid #bbb', padding: '5px 10px',
    verticalAlign: 'top',
  },

  /* EXPERIENCE ENTRY */
  expRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  expTitle:   { fontWeight: 'bold', fontSize: '11pt' },
  expDate:    { fontSize: '10pt', color: '#444', whiteSpace: 'nowrap' },
  expCompany: { fontSize: '10.5pt', fontStyle: 'italic', color: '#333', marginBottom: '2px' },

  /* PROJECT */
  projectLabel:    { fontWeight: 'bold', fontSize: '11pt', marginTop: '12px', marginBottom: '4px' },
  projectSubLabel: { fontWeight: 'bold', fontSize: '10.5pt', margin: '8px 0 3px 0' },

  /* EDUCATION / PLAIN TEXT */
  plainText: { fontSize: '10.5pt', margin: '4px 0', lineHeight: '1.6' },
};

/* ── helpers ── */
const flattenSkills = (data) => {
  if (!data) return [];
  if (Array.isArray(data))
    return data.flatMap(e =>
      typeof e === 'string' ? [e]
      : Array.isArray(e) ? e
      : typeof e === 'object' ? Object.values(e).flat()
      : []
    );
  return [];
};

const HR   = () => <hr style={S.hrThick} />;
const HRThin = () => <hr style={S.hrThin} />;
const Heading = ({ children }) => <div style={S.sectionHeading}>{children}</div>;

/* ── Table row helper ── */
const TRow = ({ label, value }) =>
  value ? (
    <tr>
      <td style={S.tdLabel}>{label}</td>
      <td style={S.tdValue}>{value}</td>
    </tr>
  ) : null;

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
export default function FormattedResumeModal({ open, onClose, candidate }) {
  if (!open || !candidate) return null;

  const skills      = flattenSkills(candidate.skills);
  const interviews  = Array.isArray(candidate.interviewDetails) ? candidate.interviewDetails : [];
  const hasPosition = candidate.currentPosition && candidate.currentPosition !== 'N/A';
  const hasOrg      = candidate.currentOrganisation && candidate.currentOrganisation !== 'N/A';
  const hasEducation= candidate.education && candidate.education !== 'N/A';
  const hasRemarks  = candidate.remarks && candidate.remarks !== 'NO Remarks Added';

  /* Split profile summary into bullet sentences */
  const summaryBullets = (candidate.profileSummary && candidate.profileSummary !== 'N/A')
    ? candidate.profileSummary.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 10)
    : [];

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.paper}>

        {/* Close */}
        <IconButton style={S.closeBtn} onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>

        <div style={S.body}>

          {/* ══ NAME & CONTACT ══ */}
          <div style={S.nameBlock}>
            <div style={S.candidateName}>{candidate.name || 'Candidate'}</div>
            <HR />
            <div style={S.contactLine}>
              {candidate.phone_number && <span>{candidate.phone_number}</span>}
              {candidate.phone_number && candidate.email && <span style={S.sep}>|</span>}
              {candidate.email && <span>{candidate.email}</span>}
              {candidate.location && <><span style={S.sep}>|</span><span>{candidate.location}</span></>}
            </div>
          </div>

          {/* ══ PROFESSIONAL SUMMARY ══ */}
          {summaryBullets.length > 0 && (
            <>
              <Heading>Professional Summary</Heading>
              <ul style={S.ul}>
                {summaryBullets.map((s, i) => <li key={i} style={S.li}>{s.trim()}</li>)}
              </ul>
            </>
          )}

          {/* ══ TECHNICAL SKILLS ══ */}
          {skills.length > 0 && (
            <>
              <Heading>Technical Skills</Heading>
              <table style={S.table}>
                <tbody>
                  <TRow label="Skills" value={skills.join(', ')} />
                  {candidate.experience && <TRow label="Total Experience" value={`${candidate.experience} year${candidate.experience !== 1 ? 's' : ''}`} />}
                  {hasOrg      && <TRow label="Current Organisation" value={candidate.currentOrganisation} />}
                  {hasPosition && <TRow label="Current Position"     value={candidate.currentPosition} />}
                  {candidate.applied_position && <TRow label="Applied For" value={candidate.applied_position} />}
                </tbody>
              </table>
            </>
          )}

          {/* ══ WORK EXPERIENCE ══ */}
          {(hasPosition || hasOrg) && (
            <>
              <Heading>Work Experience</Heading>

              {/* Current role */}
              {(hasPosition || hasOrg) && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={S.expRow}>
                    {hasPosition && <span style={S.expTitle}>{candidate.currentPosition}</span>}
                    <span style={S.expDate}>Present</span>
                  </div>
                  {hasOrg && <div style={S.expCompany}>{candidate.currentOrganisation}</div>}
                  {candidate.experience && (
                    <div style={{ fontSize: '10pt', color: '#555' }}>
                      Experience: {candidate.experience} year{candidate.experience !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ══ PROJECT DETAILS (one per interview round) ══ */}
          {interviews.length > 0 && (
            <>
              <Heading>Project / Interview Details</Heading>

              {interviews.map((iv, i) => {
                const panelists = Array.isArray(iv.interviewerNameAndEmail) ? iv.interviewerNameAndEmail : [];
                const ivDate = iv.date
                  ? new Date(iv.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                  : null;

                return (
                  <div key={i} style={{ marginBottom: '16px' }}>
                    <div style={S.projectLabel}>
                      Project / Round {i + 1}{iv.type ? ` — ${iv.type}` : ''}
                    </div>

                    {/* Project meta table */}
                    <table style={S.table}>
                      <tbody>
                        {hasOrg && <TRow label="Company Name" value={candidate.currentOrganisation} />}
                        <TRow label="Project / Role"    value={iv.role || iv.type || ''} />
                        <TRow label="Round"             value={iv.round} />
                        <TRow label="Date"              value={ivDate} />
                        <TRow label="Mode"              value={iv.mode} />
                        <TRow label="Duration"          value={iv.duration} />
                        {panelists.length > 0 && (
                          <TRow
                            label="Interviewer(s)"
                            value={panelists.map(p => `${p.name || ''}${p.email ? ` (${p.email})` : ''}`).join(', ')}
                          />
                        )}
                        {iv.meetingLink && <TRow label="Meeting Link" value={iv.meetingLink} />}
                      </tbody>
                    </table>

                    {/* Feedback as bullet */}
                    {iv.feedback && (
                      <>
                        <div style={S.projectSubLabel}>Feedback / Remarks</div>
                        <ul style={S.ul}>
                          <li style={S.li}>{iv.feedback}</li>
                        </ul>
                      </>
                    )}

                    {i < interviews.length - 1 && <HRThin />}
                  </div>
                );
              })}
            </>
          )}

          {/* ══ EDUCATION ══ */}
          {hasEducation && (
            <>
              <Heading>Education</Heading>
              <p style={S.plainText}>{candidate.education}</p>
            </>
          )}

          {/* ══ ADDITIONAL INFO ══ */}
          {(candidate.currentCtc || candidate.expectedCtc || candidate.noticePeriod || candidate.releventExp || candidate.relocate) && (
            <>
              <Heading>Additional Information</Heading>
              <table style={S.table}>
                <tbody>
                  {candidate.currentCtc  && candidate.currentCtc  !== 'N/A' && <TRow label="Current CTC"    value={candidate.currentCtc} />}
                  {candidate.expectedCtc && candidate.expectedCtc !== 'N/A' && <TRow label="Expected CTC"   value={candidate.expectedCtc} />}
                  {candidate.noticePeriod && candidate.noticePeriod !== 'N/A' && <TRow label="Notice Period" value={candidate.noticePeriod} />}
                  {candidate.relocate    && candidate.relocate    !== 'N/A' && <TRow label="Ready to Relocate" value={candidate.relocate} />}
                  {candidate.releventExp && candidate.releventExp !== 'N/A' && <TRow label="Relevant Exp."  value={candidate.releventExp} />}
                </tbody>
              </table>
            </>
          )}

          {/* ══ REMARKS ══ */}
          {hasRemarks && (
            <>
              <Heading>Remarks</Heading>
              <p style={S.plainText}>{candidate.remarks}</p>
            </>
          )}

        </div>
      </div>
    </div>
  );
}